export interface LoopOptions {
  tickMs: number;
  maxTicksPerFrame: number;
  now(): number;
  requestFrame(callback: (timeMs: number) => void): number;
  cancelFrame(handle: number): void;
}

export interface LoopCallbacks {
  // False: nothing to run yet (a guest waiting for the host). The loop stops stepping this frame and holds the interpolation.
  step(): false | undefined;
  render(alpha: number): void;
  // The exception that stops the loop, told before it surfaces.
  crashed(error: unknown): void;
}

export interface FixedStepLoop {
  start(): void;
  stop(): void;
  readonly running: boolean;
}

// Bounded like the loop: past the bound the time is dropped, not owed.
export function dueTicks(
  elapsedMs: number,
  tickMs: number,
  maxTicks: number,
): { ticks: number; spentMs: number } {
  const owed = Math.floor(Math.max(0, elapsedMs) / tickMs);
  return owed > maxTicks
    ? { ticks: maxTicks, spentMs: elapsedMs }
    : { ticks: owed, spentMs: owed * tickMs };
}

export function createFixedStepLoop(options: LoopOptions, callbacks: LoopCallbacks): FixedStepLoop {
  const { tickMs, maxTicksPerFrame } = options;
  if (!(tickMs > 0 && Number.isFinite(tickMs))) {
    throw new RangeError(`tickMs must be a positive finite number, got ${String(tickMs)}`);
  }
  if (!(Number.isInteger(maxTicksPerFrame) && maxTicksPerFrame > 0)) {
    throw new RangeError(
      `maxTicksPerFrame must be a positive integer, got ${String(maxTicksPerFrame)}`,
    );
  }

  let running = false;
  let session = 0;
  let handle: number | undefined;
  let lastTimeMs = 0;
  let accumulatorMs = 0;

  function start(): void {
    if (running) {
      return;
    }
    running = true;
    accumulatorMs = 0;
    lastTimeMs = options.now();
    handle = options.requestFrame(frame);
  }

  function stop(): void {
    if (!running) {
      return;
    }
    running = false;
    session += 1;
    if (handle !== undefined) {
      options.cancelFrame(handle);
      handle = undefined;
    }
  }

  function frame(timeMs: number): void {
    handle = undefined;
    const frameSession = session;
    try {
      advance(timeMs, frameSession);
    } catch (error) {
      stop();
      callbacks.crashed(error);
      throw error;
    }
    if (session === frameSession) {
      handle = options.requestFrame(frame);
    }
  }

  function advance(timeMs: number, frameSession: number): void {
    // The frame timestamp can precede the now() read in start().
    accumulatorMs += Math.max(0, timeMs - lastTimeMs);
    lastTimeMs = timeMs;
    let ticks = 0;
    let waiting = false;
    while (accumulatorMs >= tickMs) {
      if (ticks === maxTicksPerFrame) {
        accumulatorMs = 0;
        break;
      }
      const stepped = callbacks.step();
      if (session !== frameSession) {
        return;
      }
      if (stepped === false) {
        // Keep one tick of credit: the step runs the frame the missing input arrives, and the wait is not owed.
        accumulatorMs = tickMs;
        waiting = true;
        break;
      }
      accumulatorMs -= tickMs;
      ticks += 1;
    }
    callbacks.render(waiting ? 1 : accumulatorMs / tickMs);
  }

  return {
    start,
    stop,
    get running() {
      return running;
    },
  };
}
